/**
 * Utility functions for coordinate conversions in the annotation editor
 * Preserves exact logic from original implementation
 */

/**
 * Normalized coordinate space bounds
 * Annotations use a 0-1000 normalized space for resolution independence
 */
export const NORMALIZED_BOUNDS = {
  MIN: 0,
  MAX: 1000,
} as const;

/**
 * Convert screen coordinates to normalized 0-1000 space relative to image
 */
export const screenToNormalized = (
  screenX: number,
  screenY: number,
  rectX: number,
  rectY: number,
  rectWidth: number,
  rectHeight: number,
) => {
  // Protect against division by zero
  const safeWidth = rectWidth || 1;
  const safeHeight = rectHeight || 1;

  const normalizedX = ((screenX - rectX) / safeWidth) * NORMALIZED_BOUNDS.MAX;
  const normalizedY = ((screenY - rectY) / safeHeight) * NORMALIZED_BOUNDS.MAX;

  // Round to eliminate floating-point precision errors and clamp to valid range
  const roundedX = Math.round(normalizedX * 10000) / 10000; // Round to 4 decimal places
  const roundedY = Math.round(normalizedY * 10000) / 10000;

  // Clamp values to valid range and fix near-zero values
  const clampedX = Math.max(
    NORMALIZED_BOUNDS.MIN,
    Math.min(NORMALIZED_BOUNDS.MAX, Math.abs(roundedX) < 1e-10 ? 0 : roundedX),
  );
  const clampedY = Math.max(
    NORMALIZED_BOUNDS.MIN,
    Math.min(NORMALIZED_BOUNDS.MAX, Math.abs(roundedY) < 1e-10 ? 0 : roundedY),
  );

  return { x: clampedX, y: clampedY };
};

/**
 * Convert normalized coordinates back to screen space
 */
export const normalizedToScreen = (
  normX: number,
  normY: number,
  rectX: number,
  rectY: number,
  rectWidth: number,
  rectHeight: number,
) => {
  const screenX = (normX / NORMALIZED_BOUNDS.MAX) * rectWidth + rectX;
  const screenY = (normY / NORMALIZED_BOUNDS.MAX) * rectHeight + rectY;
  return { x: screenX, y: screenY };
};

/**
 * Calculate drag boundaries for boxes to keep them within image
 */
export const calculateBoxDragBounds = (
  pos: { x: number; y: number },
  screenPos: { x: number; y: number },
  screenWidth: number,
  screenHeight: number,
  rectX: number,
  rectY: number,
  rectWidth: number,
  rectHeight: number,
) => {
  const minX = rectX - screenPos.x;
  const maxX = rectX + rectWidth - screenWidth - screenPos.x;
  const minY = rectY - screenPos.y;
  const maxY = rectY + rectHeight - screenHeight - screenPos.y;

  return {
    x: Math.max(minX, Math.min(maxX, pos.x)),
    y: Math.max(minY, Math.min(maxY, pos.y)),
  };
};

/**
 * Calculate drag boundaries for points to keep them within image
 */
export const calculatePointDragBounds = (
  pos: { x: number; y: number },
  screenPos: { x: number; y: number },
  rectX: number,
  rectY: number,
  rectWidth: number,
  rectHeight: number,
) => {
  const minX = rectX - screenPos.x;
  const maxX = rectX + rectWidth - screenPos.x;
  const minY = rectY - screenPos.y;
  const maxY = rectY + rectHeight - screenPos.y;

  return {
    x: Math.max(minX, Math.min(maxX, pos.x)),
    y: Math.max(minY, Math.min(maxY, pos.y)),
  };
};
